/**
 * Last.fm Service
 *
 * Fetches artist info, fan trends, and top tracks from Last.fm API via backend proxy.
 * Used for Cultural Intelligence panel trend and mood display.
 */

import { apiClient } from '@shared/services/apiClient';

export interface LastFmTrack {
  name: string;
  playcount: number;
  listeners: number;
  url: string;
}

export interface LastFmTag {
  name: string;
  url: string;
}

export interface LastFmArtistInfo {
  name: string;
  listeners: number;
  playcount: number;
  bio: string | null;
  tags: LastFmTag[];
  topTracks: LastFmTrack[];
  similarArtists: string[];
  imageUrl: string | null;
}

export interface FanTrendData {
  artistName: string;
  totalListeners: number;
  totalPlaycount: number;
  dominantMood: string;
  trendingTracks: LastFmTrack[];
  topTags: string[];
}

// Simple in-memory cache
const artistCache: Map<string, { data: LastFmArtistInfo; expiresAt: number }> = new Map();
const trendCache: Map<string, { data: FanTrendData; expiresAt: number }> = new Map();
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Get comprehensive artist info from Last.fm
 *
 * @param artistName - Name of the artist
 * @returns Artist info or null if not found
 */
export async function getArtistInfo(artistName: string): Promise<LastFmArtistInfo | null> {
  const cacheKey = artistName.toLowerCase();

  // Check cache
  const cached = artistCache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.data;
  }

  try {
    const response = await apiClient.get<LastFmArtistInfo>('/lastfm/artist-info', {
      params: { artist_name: artistName },
    });

    // Cache the result
    artistCache.set(cacheKey, {
      data: response.data,
      expiresAt: Date.now() + CACHE_TTL_MS,
    });

    return response.data;
  } catch (error) {
    console.error('[Last.fm] Failed to fetch artist info:', error);
    return null;
  }
}

/**
 * Get fan trend data for an artist
 *
 * @param artistName - Name of the artist
 * @returns Fan trend data or null if not found
 */
export async function getFanTrends(artistName: string): Promise<FanTrendData | null> {
  const cacheKey = artistName.toLowerCase();

  // Check cache
  const cached = trendCache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.data;
  }

  try {
    const response = await apiClient.get<FanTrendData>('/lastfm/fan-trends', {
      params: { artist_name: artistName },
    });

    // Cache the result
    trendCache.set(cacheKey, {
      data: response.data,
      expiresAt: Date.now() + CACHE_TTL_MS,
    });

    return response.data;
  } catch (error) {
    console.error('[Last.fm] Failed to fetch fan trends:', error);
    return null;
  }
}

/**
 * Format large numbers with K/M suffix
 */
export function formatNumber(num: number): string {
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  } else if (num >= 1_000) {
    return `${(num / 1_000).toFixed(0)}K`;
  }
  return num.toString();
}

/**
 * Get mood color class based on mood name
 */
export function getMoodColorClass(mood: string): string {
  const moodColors: Record<string, string> = {
    Hype: 'text-amber-400',
    Electric: 'text-cyan-400',
    Party: 'text-pink-400',
    Upbeat: 'text-green-400',
    Energetic: 'text-orange-400',
    Intense: 'text-red-400',
    Raw: 'text-red-500',
    Smooth: 'text-purple-400',
    Soulful: 'text-violet-400',
    Mellow: 'text-blue-400',
    Deep: 'text-indigo-400',
    Intimate: 'text-rose-400',
    Chill: 'text-teal-400',
    Warm: 'text-amber-300',
    Passionate: 'text-red-400',
    Hot: 'text-orange-500',
    Heartfelt: 'text-rose-300',
    Moody: 'text-slate-400',
    Vibing: 'text-purple-300',
  };

  return moodColors[mood] || 'text-purple-400';
}
